import { Component, Suspense, lazy } from 'react'

import { SensorList } from './SensorList'

import type { ErrorInfo, ReactNode } from 'react'

export interface BrainScanProps {
  /** CSS pixels. The renderer owns the backing store, so this is a number. */
  size: number
  /** A sampling target, not a cap: 2400 draws 2,540 of the shipped 12,700. */
  maxPoints: number
  showLabels?: boolean
  /**
   * Pointer input. Defaults true, so no existing call site changes.
   *
   * `false` is the decoration mode: no drag, no tap-to-popover, and the cursor
   * stays an arrow. It governs input only -- the brain still drifts, because
   * stillness is `prefers-reduced-motion`'s business and already handled.
   */
  interactive?: boolean
  className?: string
}

/**
 * The eager shell, and the only public component here.
 *
 * `/* @__PURE__ *\/` on the `lazy` call lets a bundle that imports this module
 * and never renders it drop the call, and with it the whole chunk.
 */
const BrainCanvas = /* @__PURE__ */ lazy(() =>
  import('./BrainCanvas').then((m) => ({ default: m.BrainCanvas })),
)

interface BoundaryProps {
  fallback: ReactNode
  children: ReactNode
}

/**
 * A LOCAL boundary, because the app-level one replaces the whole screen.
 *
 * A `React.lazy` import that fails -- the chunk 404s during a rollout, the
 * network drops -- throws during render and reaches the nearest boundary. With
 * only `src/components/ErrorBoundary.tsx` above it, a failed DECORATION would
 * replace a person's reading with "Something went wrong". Here it renders the
 * same placeholder the pending state uses, and the page carries on.
 */
class BrainBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('BrainScan could not load', error, info.componentStack)
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

export function BrainScan({ size, maxPoints, showLabels = true, interactive = true, className }: BrainScanProps) {
  /**
   * The same box as the canvas, so nothing reflows when the chunk arrives.
   * NOT `role="status"`: a decoration loading is not a status, and announcing
   * it would be the only thing a screen reader heard about a picture it cannot
   * see. The eight pairs below are what it gets instead.
   */
  const placeholder = (
    <div
      className="brain-scan__placeholder"
      aria-hidden="true"
      style={{ inlineSize: size, blockSize: size }}
    />
  )

  return (
    <div
      className={className === undefined ? 'brain-scan' : `brain-scan ${className}`}
      style={{ position: 'relative', flex: 'none', inlineSize: size, blockSize: size }}
    >
      <BrainBoundary fallback={placeholder}>
        <Suspense fallback={placeholder}>
          <BrainCanvas size={size} maxPoints={maxPoints} showLabels={showLabels} interactive={interactive} />
        </Suspense>
      </BrainBoundary>
      <SensorList />
    </div>
  )
}
